<template>
  <div
    ref="rightPanel"
    :class="{ show: show }"
    :style="css"
    class="rightPanel-container"
  >
    <div v-if="backdrop" class="rightPanel-background" />
    <div class="rightPanel">
      <div
        class="handle-button"
        :style="{ top: buttonTop + 'px' }"
        @click.stop="toggle"
      >
        <el-badge :hidden="count < 1" :value="count">
          <el-icon>
            <CloseElementIcon v-if="show" />
            <component :is="icon" v-else />
          </el-icon>
        </el-badge>
      </div>
      <div class="rightPanel-items">
        <slot />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { addClass, removeClass } from '@/utils'
import {
  defineComponent,
  computed,
  ref,
  onMounted,
  watch,
  onBeforeUnmount
} from 'vue'
import { useCookies } from 'vue3-cookies'
import useRouter from '@/hooks/useRouter'
import { useFaqsStore } from '@/store/modules/faqs'

export default defineComponent({
  name: 'RightPanel',
  props: {
    clickNotClose: {
      default: false,
      type: Boolean
    },
    showByDefault: {
      default: false,
      type: Boolean
    },
    backdrop: {
      default: true,
      type: Boolean
    },
    once: {
      default: false,
      type: Boolean
    },
    count: {
      default: 0,
      type: Number
    },
    id: {
      default: '',
      type: String
    },
    icon: {
      default: 'OperationElementIcon',
      type: String
    },
    buttonTop: {
      default: 80,
      type: Number
    },
    maxWidth: {
      default: 250,
      type: Number
    },
    panelType: {
      default: '',
      type: String
    },
    faqPage: {
      default: '',
      type: String
    }
  },
  setup(props) {
    const faqsStore = useFaqsStore()
    const { route } = useRouter()
    const { cookies } = useCookies()
    const cookiesKey = computed(() => {
      return route.value.path.replaceAll('/', '')
    })
    const showByDefault = computed(() => {
      return (
        (props.showByDefault && !props.once) ||
        (props.once && !cookies.get(cookiesKey.value))
      )
    })
    const show = ref(showByDefault.value)
    const rightPanel = ref<HTMLElement | null>(null)

    const toggle = (arg: any) => {
      show.value = !show.value
      if (!show.value) {
        cookies.set(cookiesKey.value, true)
      }
    }

    const closeSidebar = (evt: Event) => {
      const parent = (evt.target as HTMLElement)?.closest('.rightPanel')
      if (!parent) {
        show.value = false
        window.removeEventListener('click', closeSidebar)
      }
    }

    const addEventClick = () => {
      window.addEventListener('click', closeSidebar)
    }

    const insertToBody = () => {
      const body = document.querySelector('body') as HTMLElement
      if (rightPanel.value) {
        body.insertBefore(rightPanel.value, body?.firstChild)
      }
    }

    watch(showByDefault, (value) => {
      show.value = value
    })

    watch(show, (value) => {
      if (value && !props.clickNotClose) {
        if (props.panelType === 'faq' && props.faqPage !== '') {
          faqsStore.SendStat({ hit: true, page: props.faqPage })
        }
        addEventClick()
      }
      if (value) {
        addClass(document.body, 'showRightPanel')
      } else {
        removeClass(document.body, 'showRightPanel')
      }
    })

    onMounted(() => {
      insertToBody()
    })

    onBeforeUnmount(() => {
      rightPanel.value?.remove()
    })

    // @ts-ignore: No overload matches this call.
    watch(
      (_: any) => props.id,
      (newVal: string, oldVal: string) => {
        if (newVal !== oldVal) {
          show.value = !show.value
        }
      }
    )

    const css = computed(() => {
      return {
        '--min-label-width': `${props.maxWidth}px`
      }
    })

    if (
      showByDefault.value &&
      props.panelType === 'faq' &&
      props.faqPage !== ''
    ) {
      faqsStore.SendStat({ hit: true, page: props.faqPage })
    }

    return { rightPanel, show, css, toggle }
  }
})
</script>

<style>
.showRightPanel {
  overflow: hidden;
  position: relative;
  width: calc(100% - 15px);
}
</style>

<style lang="scss" scoped>
.rightPanel-background {
  position: fixed;
  top: 0;
  left: 0;
  opacity: 0;
  transition: opacity 0.3s cubic-bezier(0.7, 0.3, 0.1, 1);
  background: rgba(0, 0, 0, 0.2);
  z-index: -1;
}

.rightPanel {
  width: 100%;
  max-width: var(--min-label-width);
  height: 100vh;
  position: fixed;
  top: 0;
  right: 0;
  box-shadow: 0px 0px 15px 0px rgba(0, 0, 0, 0.05);
  transition: all 0.25s cubic-bezier(0.7, 0.3, 0.1, 1);
  transform: translate(100%);
  background: #fff;
  z-index: 1000;
}
.rightPanel-items {
  overflow-y: scroll;
  overflow-x: hidden;
  height: 100vh;
}

.show {
  transition: all 0.3s cubic-bezier(0.7, 0.3, 0.1, 1);

  .rightPanel-background {
    z-index: 999;
    opacity: 1;
    width: 100%;
    height: 100%;
  }

  .rightPanel {
    transform: translate(0);
  }
}

.handle-button {
  width: 48px;
  height: 48px;
  position: absolute;
  left: -48px;
  text-align: center;
  font-size: 24px;
  border-radius: 6px 0 0 6px !important;
  z-index: 0;
  pointer-events: auto;
  cursor: pointer;
  color: #fff;
  line-height: 48px;
  background-color: var(--el-color-primary);
  i {
    font-size: 24px;
    line-height: 24px;
  }
  .el-badge {
    vertical-align: top;
    height: 100%;
    width: 100%;
  }
}
</style>
